import { useContext, useEffect, useMemo, useState } from "react"
import { FirebaseContext } from "../firebase"
import { UserContext } from "../context/userContext"
import { LogError } from "../helpers/logger"

function doesTemplateMatchFilter(template, filterObj = {}) {
  // Support flexible matching (e.g. by template_id or template_title) using key/value pairs.
  const filterEntries = Object.entries(filterObj)

  return filterEntries.every(([field, value]) => template?.[field] === value)
}

export default function useTemplateAvailability({ reloadDeps = null } = {}) {
  const firebase = useContext(FirebaseContext)
  const userCtx = useContext(UserContext)
  const enterpriseId = userCtx?.session?.user?.enterprise_id

  const [customTemplates, setCustomTemplates] = useState([])
  const [systemTemplates, setSystemTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)

  const combinedTemplates = useMemo(() => {
    // Custom templates override same-id system templates.
    const customIds = new Set(customTemplates.map((item) => item.template_id))
    const filteredSystem = systemTemplates.filter(
      (item) => !customIds.has(item.template_id),
    )

    // Keep stable ordering parity with previous NavMain logic.
    return [...customTemplates, ...filteredSystem].sort((a, b) =>
      `${a?.template_id || ""}`.localeCompare(`${b?.template_id || ""}`),
    )
  }, [customTemplates, systemTemplates])

  useEffect(() => {
    let isMounted = true

    const loadTemplates = async () => {
      if (!firebase?.db || !enterpriseId) {
        // When session/db context is not ready, expose an empty non-loading state.
        if (isMounted) {
          setCustomTemplates([])
          setSystemTemplates([])
          setLoadingTemplates(false)
        }
        return
      }

      setLoadingTemplates(true)

      try {
        // Load both sources together so availability checks use a consistent snapshot.
        const [systemSnapshot, customSnapshot] = await Promise.all([
          firebase.db.collection("system-templates/").get(),
          firebase.db
            .collection(`enterprise-config/${enterpriseId}/custom-templates/`)
            .get(),
        ])

        if (!isMounted) {
          return
        }

        setSystemTemplates(
          systemSnapshot.docs.map((doc) => ({
            template_id: doc.id,
            ...doc.data(),
          })),
        )

        setCustomTemplates(
          customSnapshot.docs.map((doc) => ({
            template_id: doc.id,
            ...doc.data(),
          })),
        )
      } catch (error) {
        LogError("Failed to load templates:", error)
      } finally {
        if (isMounted) {
          setLoadingTemplates(false)
        }
      }
    }

    loadTemplates()

    return () => {
      // Guard against state updates from in-flight requests after unmount.
      isMounted = false
    }
    // reloadDeps allows callers to opt into external refresh triggers.
  }, [firebase?.db, enterpriseId, reloadDeps])

  const isTemplateAvailable = (filterObj) => {
    const template = combinedTemplates.find((item) =>
      doesTemplateMatchFilter(item, filterObj),
    )

    if (!template) {
      // Preserve existing behavior: unknown template rows remain visible.
      return true
    }

    return template.template_availability === 1
  }

  return {
    customTemplates,
    loadingTemplates,
    isTemplateAvailable,
  }
}
